/* eslint-disable unicorn/prefer-dom-node-append */
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { SITE } from '../data/site.data';
import { canonicalUrl } from '../data/url.utility';
import { SeoData } from './seo-data';

const SEO_STATE_KEY = makeStateKey<string>('lumiron-seo');
const JSON_LD_ID = 'ld-route';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private appliedKey: string | undefined;

  constructor() {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(SEO_STATE_KEY)) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      this.appliedKey = this.transferState.get(SEO_STATE_KEY, undefined);
      this.transferState.remove(SEO_STATE_KEY);
    }
  }

  apply(seo: SeoData, url: string): void {
    const fullUrl = canonicalUrl(url);
    const key = `${fullUrl}::${seo.title}`;
    if (this.appliedKey === key) {
      return;
    }
    this.appliedKey = key;

    const image = seo.image ?? SITE.defaultImage;
    const robots = seo.robots ?? 'index, follow';

    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ name: 'robots', content: robots });

    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:url', content: fullUrl });
    this.meta.updateTag({ property: 'og:image', content: image });

    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.updateCanonical(fullUrl);
    this.updateJsonLd(seo.jsonLd?.());

    if (isPlatformServer(this.platformId)) {
      this.transferState.set(SEO_STATE_KEY, key);
    }
  }

  private updateCanonical(url: string): void {
    const head = this.document.head;
    let link: HTMLLinkElement | null = head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    if (link.getAttribute('href') !== url) {
      link.setAttribute('href', url);
    }
  }

  private updateJsonLd(payload: object | readonly object[] | undefined): void {
    const head = this.document.head;
    const existing = head.querySelector(`script[type="application/ld+json"]#${JSON_LD_ID}`);
    if (!payload) {
      if (existing) existing.remove();
      return;
    }
    const json = JSON.stringify(payload);
    if (existing && existing.textContent === json) {
      return;
    }
    if (existing) existing.remove();
    const script = this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', JSON_LD_ID);
    script.textContent = json;
    head.appendChild(script);
  }
}
